<template>
  <!-- eslint-disable vue/max-attributes-per-line -->
  <div>
    <v-breadcrumbs :items="breadcrumbs" divider="/" class="pa-0 mb-4">
      <template #item="{ item }">
        <v-breadcrumbs-item :to="item.to" :disabled="item.disabled" exact>
          {{ item.text }}
        </v-breadcrumbs-item>
      </template>
    </v-breadcrumbs>

    <div class="d-flex align-center mb-5 flex-wrap" style="gap: 8px">
      <h1 class="text-2xl mr-2">
        Льгота #{{ benefitId }}
      </h1>
      <benefit-status-chip v-if="benefitItem" :status="benefitItem.status" />
      <v-spacer />
      <v-btn
        icon
        :disabled="!benefitItem"
        :aria-label="`Логи льготы #${benefitId}`"
        @click="openLogsModal"
      >
        <v-icon>{{ mdiHistory }}</v-icon>
      </v-btn>
      <v-btn icon :aria-label="'Вернуться к списку льгот'" @click="goBack">
        <v-icon>{{ mdiClose }}</v-icon>
      </v-btn>
    </div>

    <v-row v-if="loading">
      <v-col cols="12" md="5">
        <v-skeleton-loader type="card" />
      </v-col>
      <v-col cols="12" md="7">
        <v-skeleton-loader type="card" />
      </v-col>
    </v-row>

    <v-alert v-else-if="!benefitItem" type="warning" outlined>
      Льгота не найдена или недоступна.
      <v-btn text small class="ml-2" @click="goBack">
        К списку
      </v-btn>
    </v-alert>

    <v-row v-else>
      <v-col cols="12" md="5">
        <v-card outlined>
          <v-card-title class="pb-1">
            Информация
          </v-card-title>
          <v-divider />
          <v-card-text class="pt-4">
            <div class="mb-2">
              <div class="text-caption text--secondary">
                Пользователь
              </div>
              <div class="font-weight-medium">
                {{ formatUserName(benefitItem) }}
              </div>
              <div class="text-caption text--secondary">
                {{ benefitItem.user_phone || benefitItem.user_email || '—' }}
              </div>
            </div>
            <div class="mb-2">
              <div class="text-caption text--secondary">
                Категория
              </div>
              <div>{{ benefitItem.category_name || '—' }}</div>
            </div>
            <div class="mb-2">
              <div class="text-caption text--secondary">
                Период
              </div>
              <div>
                {{ formatDateTime(benefitItem.active_from) || '—' }} — {{ formatDateTime(benefitItem.active_to) || '—' }}
              </div>
            </div>
            <div class="mb-2">
              <div class="text-caption text--secondary">
                Документ
              </div>
              <benefit-document-preview :url="benefitItem.document_url" />
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" md="7">
        <v-card outlined>
          <v-card-title class="pb-1">
            Редактирование
          </v-card-title>
          <v-divider />
          <v-card-text class="pt-4">
            <v-text-field
              :value="selectedUserLabel"
              dense
              outlined
              readonly
              disabled
              label="Пользователь"
              class="mb-3"
            />

            <v-row dense>
              <v-col cols="12" sm="6">
                <v-select
                  v-model="form.category_id"
                  :items="categoryItems"
                  item-text="name"
                  item-value="id"
                  label="Категория льготы"
                  dense
                  outlined
                  clearable
                />
              </v-col>
              <v-col cols="12" sm="6">
                <v-select
                  v-model="form.status"
                  :items="statusItems"
                  item-text="text"
                  item-value="value"
                  label="Статус"
                  dense
                  outlined
                />
              </v-col>
            </v-row>

            <v-row dense class="mb-1">
              <v-col cols="12" sm="6">
                <BenefitDateField
                  v-model="form.active_from"
                  label="Действует с"
                  persistent-hint
                  :error-messages="activeFromError"
                />
              </v-col>
              <v-col cols="12" sm="6">
                <BenefitDateField
                  v-model="form.active_to"
                  label="Действует по"
                  persistent-hint
                  :error-messages="activeToError"
                />
              </v-col>
            </v-row>

            <v-textarea v-model="form.comment" label="Комментарий" rows="3" dense outlined class="mt-3" />
            <v-file-input v-model="form.document" label="Новый документ" dense outlined show-size />
            <v-checkbox v-model="form.clear_document" label="Удалить текущий документ" dense hide-details class="mt-1" />
          </v-card-text>
          <v-divider />
          <v-card-actions class="pa-4">
            <v-spacer />
            <v-btn color="error" :disabled="saving" @click="goBack">
              Отмена
            </v-btn>
            <v-btn color="primary" :loading="saving" @click="submit">
              Сохранить
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>

    <v-row v-if="benefitItem">
      <v-col cols="12">
        <v-card outlined>
          <v-card-title class="pb-1">
            Льготы по категории
            <span v-if="categoryRulesName" class="text-body-2 text--secondary ml-2">
              {{ categoryRulesName }}
            </span>
          </v-card-title>
          <v-divider />
          <v-card-text class="pt-3">
            <v-skeleton-loader v-if="categoryRulesLoading" type="table-row@3" />
            <v-alert
              v-else-if="!form.category_id"
              type="info"
              outlined
              dense
              class="mb-0"
            >
              Выберите категорию льготы, чтобы увидеть доступные скидки.
            </v-alert>
            <v-alert
              v-else-if="!categoryRules.length"
              type="warning"
              outlined
              dense
              class="mb-0"
            >
              Для выбранной категории нет активных правил скидок.
            </v-alert>
            <v-data-table
              v-else
              :headers="categoryRulesHeaders"
              :items="categoryRules"
              dense
              hide-default-footer
            >
              <template #[`item.target_name`]="{ item }">
                {{ formatRuleTarget(item) }}
              </template>
              <template #[`item.discount_display`]="{ item }">
                <span class="font-weight-medium">{{ item.discount_display }}</span>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-dialog v-model="logsDialog" max-width="960" scrollable>
      <v-card>
        <v-card-title>
          Логи льготы #{{ benefitId }}
          <v-spacer />
          <v-btn icon aria-label="Закрыть" @click="logsDialog = false">
            <v-icon>{{ mdiClose }}</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider />
        <v-card-text class="pt-3">
          <v-data-table
            :headers="logModalHeaders"
            :items="logs"
            :loading="logsLoading"
            dense
            hide-default-footer
          >
            <template #[`item.action_display`]="{ item }">
              <benefit-log-action-chip :action="item.action" />
            </template>
            <template #[`item.created_at`]="{ item }">
              {{ formatDateTime(item.created_at) }}
            </template>
            <template #[`item.log_subject`]="{ item }">
              {{ formatLogSubject(item) }}
            </template>
            <template #[`item.performed_by_name`]="{ item }">
              {{ formatLogActor(item) }}
            </template>
          </v-data-table>
          <div
            v-if="logsMeta.num_pages > 1"
            class="pt-4 d-flex align-center flex-wrap"
          >
            <span class="mr-4 text--secondary">Всего: {{ logsMeta.count }}</span>
            <v-pagination
              v-model="logsPage"
              :length="logsMeta.num_pages"
              :disabled="logsLoading"
              @input="fetchBenefitLogs"
            />
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar" :color="snackbarColor" timeout="5000">
      {{ snackbarText }}
      <template #action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar = false">
          OK
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import { mdiClose, mdiHistory } from '@mdi/js'
import Api from '@/api/api'
import {
  formatDateTime,
  formatLogActor,
  formatLogSubject,
  stripEmptyParams,
  isValidIsoDate,
  isoDateRangeError,
} from './utils'
import BenefitStatusChip from './components/BenefitStatusChip.vue'
import BenefitDateField from './components/BenefitDateField.vue'
import BenefitDocumentPreview from './components/BenefitDocumentPreview.vue'
import BenefitLogActionChip from './components/BenefitLogActionChip.vue'

export default {
  name: 'UserBenefitProcess',
  components: {
    BenefitStatusChip,
    BenefitDocumentPreview,
    BenefitLogActionChip,
    BenefitDateField,
  },
  data() {
    return {
      mdiClose,
      mdiHistory,
      loading: false,
      saving: false,
      benefitItem: null,
      categoryItems: [],
      statusItems: [
        { text: 'Активна', value: 'active' },
        { text: 'Неактивна', value: 'inactive' },
        { text: 'Истекла', value: 'expired' },
      ],
      form: {
        user_id: '',
        category_id: '',
        status: 'active',
        active_from: '',
        active_to: '',
        comment: '',
        document: null,
        clear_document: false,
      },
      logsDialog: false,
      logsLoading: false,
      logs: [],
      logsPage: 1,
      logsPageSize: 50,
      logsMeta: { count: 0, num_pages: 0, current_page: 1 },
      logModalHeaders: [
        { text: 'Время', value: 'created_at', width: 140 },
        { text: 'Действие', value: 'action_display', sortable: false },
        { text: 'Объект', value: 'log_subject', sortable: false },
        { text: 'Комментарий', value: 'comment' },
        { text: 'Кто', value: 'performed_by_name' },
      ],
      snackbar: false,
      snackbarText: '',
      snackbarColor: 'primary',
      categoryRulesLoading: false,
      categoryRules: [],
      categoryRulesName: '',
      categoryRulesHeaders: [
        { text: 'Правило', value: 'name' },
        { text: 'Объект', value: 'target_name', sortable: false },
        { text: 'Скидка', value: 'discount_display', sortable: false, width: 120 },
      ],
    }
  },
  computed: {
    benefitId() {
      return this.$route.params.id
    },
    selectedUserLabel() {
      if (!this.benefitItem) return '—'
      return `${this.benefitItem.user_id} — ${this.formatUserName(this.benefitItem)} (${this.benefitItem.user_phone || this.benefitItem.user_email || '—'})`
    },
    breadcrumbs() {
      return [
        { text: 'Льготы', to: '/benefits/user-benefits', disabled: false },
        { text: 'Льготы пользователей', to: '/benefits/user-benefits', disabled: false },
        { text: `#${this.benefitId}`, disabled: true },
      ]
    },
    activeFromError() {
      if (!this.form.active_from) return []
      return isValidIsoDate(this.form.active_from)
        ? []
        : ['Укажите дату в формате YYYY-MM-DD']
    },
    activeToError() {
      if (!this.form.active_to) return []
      if (!isValidIsoDate(this.form.active_to)) return ['Укажите дату в формате YYYY-MM-DD']
      return isoDateRangeError(this.form.active_from, this.form.active_to)
    },
  },
  async created() {
    await this.loadCategories()
    await this.loadBenefit()
    await this.loadCategoryRules()
  },
  watch: {
    'form.category_id': {
      handler() {
        this.loadCategoryRules()
      },
    },
  },
  methods: {
    formatDateTime,
    formatLogActor,
    formatLogSubject,
    toast(msg, color = 'primary') {
      this.snackbarText = msg
      this.snackbarColor = color
      this.snackbar = true
    },
    apiError(err, fallback = 'Ошибка запроса') {
      const d = err.response && err.response.data
      const msg = (d && (d.error || d.message)) || (d && d.data && d.data.error) || err.message || fallback
      this.toast(msg, 'error')
    },
    formatUserName(item) {
      if (!item) return '—'
      const full = [item.user_last_name, item.user_first_name].filter(Boolean).join(' ').trim()
      return full || item.user_email || `ID ${item.user_id}`
    },
    formatRuleTarget(rule) {
      if (!rule) return '—'
      if (rule.target_name) return rule.target_name
      return '—'
    },
    async loadCategoryRules() {
      if (!this.form.category_id) {
        this.categoryRules = []
        this.categoryRulesName = ''
        return
      }
      this.categoryRulesLoading = true
      try {
        const res = await Api.getBenefitRules({ category_id: this.form.category_id })
        if (!res.success) {
          this.categoryRules = []
          this.categoryRulesName = ''
          return
        }
        const data = res.data || {}
        this.categoryRules = data.rules || []
        this.categoryRulesName = data.category_name || ''
      } catch (e) {
        this.categoryRules = []
        this.categoryRulesName = ''
        this.apiError(e, 'Не удалось загрузить правила льгот')
      } finally {
        this.categoryRulesLoading = false
      }
    },
    async loadCategories() {
      try {
        const res = await Api.getBenefitCategories()
        if (!res.success) return
        this.categoryItems = res.data || []
      } catch (e) {
        this.apiError(e)
      }
    },
    async loadBenefit() {
      this.loading = true
      try {
        const res = await Api.getUserBenefitDetail(this.benefitId)
        if (!res.success) {
          this.toast(res.error || 'Не удалось загрузить льготу', 'error')
          return
        }
        this.benefitItem = res.data || null
        if (this.benefitItem) {
          this.form = {
            user_id: this.benefitItem.user_id || '',
            category_id: this.benefitItem.category_id || '',
            status: this.benefitItem.status || 'active',
            active_from: (this.benefitItem.active_from || '').slice(0, 10),
            active_to: (this.benefitItem.active_to || '').slice(0, 10),
            comment: this.benefitItem.comment || '',
            document: null,
            clear_document: false,
          }
        }
      } catch (e) {
        this.apiError(e)
      } finally {
        this.loading = false
      }
    },
    async submit() {
      if (!this.form.user_id || !this.form.category_id || !this.form.status) {
        this.toast('Заполните пользователя, категорию и статус', 'error')
        return
      }
      if (this.activeFromError.length || this.activeToError.length) return
      this.saving = true
      try {
        const hasFile = this.form.document instanceof File
        let payload
        if (hasFile) {
          payload = new FormData()
          payload.append('user_id', String(this.form.user_id))
          payload.append('category_id', String(this.form.category_id))
          payload.append('status', this.form.status)
          if (this.form.active_from) payload.append('active_from', this.form.active_from)
          if (this.form.active_to) payload.append('active_to', this.form.active_to)
          if (this.form.comment) payload.append('comment', this.form.comment)
          if (this.form.clear_document) payload.append('clear_document', 'true')
          payload.append('document', this.form.document)
        } else {
          payload = stripEmptyParams({
            user_id: this.form.user_id,
            category_id: this.form.category_id,
            status: this.form.status,
            active_from: this.form.active_from || undefined,
            active_to: this.form.active_to || undefined,
            comment: this.form.comment || '',
            clear_document: this.form.clear_document ? true : undefined,
          })
        }
        const res = await Api.updateUserBenefit(this.benefitId, payload)
        if (!res.success) {
          this.toast(res.error || 'Не удалось сохранить изменения', 'error')
          return
        }
        this.toast('Льгота обновлена', 'success')
        await this.loadBenefit()
        await this.loadCategoryRules()
      } catch (e) {
        this.apiError(e)
      } finally {
        this.saving = false
      }
    },
    openLogsModal() {
      if (!this.benefitItem) return
      this.logsDialog = true
      this.logsPage = 1
      this.fetchBenefitLogs()
    },
    async fetchBenefitLogs() {
      if (!this.benefitItem) return
      this.logsLoading = true
      try {
        const params = stripEmptyParams({
          benefit_id: this.benefitItem.id,
          page: this.logsPage,
          page_size: this.logsPageSize,
        })
        const res = await Api.getBenefitLogs(params)
        if (!res.success) {
          this.toast(res.error || 'Не удалось загрузить логи', 'error')
          return
        }
        const data = res.data || {}
        this.logs = data.logs || []
        this.logsMeta = {
          count: data.count || 0,
          num_pages: data.num_pages || 0,
          current_page: data.current_page || this.logsPage,
        }
      } catch (e) {
        this.apiError(e)
      } finally {
        this.logsLoading = false
      }
    },
    goBack() {
      this.$router.push('/benefits/user-benefits')
    },
  },
}
</script>
